import React, { Suspense, useEffect, useState, useCallback, useMemo } from 'react';
import { Outlet, NavLink, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Bell, Menu, X, ChevronDown, Phone, Mail, User, Copy } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { getPartnerPageTitle } from '../lib/partnerPageTitle';

const sidebarLinks = [
  {
    to: '/partner', label: 'Dashboard', end: true,
    icon: <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
  },
  {
    to: '/partner/passbook', label: 'Commission Passbook', end: false,
    icon: <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></svg>
  },
  {
    to: '/partner/calculator', label: 'Payout Rate Card', end: false,
    icon: <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="10" y2="10"/><line x1="14" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="10" y2="14"/><line x1="14" y1="14" x2="16" y2="14"/><line x1="8" y1="18" x2="10" y2="18"/><line x1="14" y1="18" x2="16" y2="18"/></svg>
  },
  {
    to: '/partner/profile', label: 'My Profile', end: false,
    icon: <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/><circle cx="12" cy="10" r="2.5"/><path d="M15 16a3 3 0 0 0-6 0"/></svg>
  },
  {
    to: '/partner/visiting-card', label: 'Visiting Card', end: false, eligibleOnly: true,
    icon: <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M8 11h8"/><path d="M8 15h5"/><circle cx="16.5" cy="9.5" r="1.5"/></svg>
  },
];

export default function PartnerLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [rmDropdownOpen, setRmDropdownOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [rmInfo, setRmInfo] = useState(null);
  const [partnerStatus, setPartnerStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [headerPhotoUrl, setHeaderPhotoUrl] = useState(null);
  const pageTitle = useMemo(() => getPartnerPageTitle(location.pathname), [location.pathname]);

  // Fetch unread notification count
  const fetchUnreadCount = useCallback(() => {
    api.get('/api/notifications?page_size=1').then(res => setUnreadCount(res.data.unread || 0)).catch(() => {});
  }, []);

  // Check partner status on mount
  useEffect(() => {
    // Skip status check if coming from registration (user data is fresh)
    const isFromRegistration = location.state?.fromRegistration;
    
    if (user?.partner_id) {
      if (isFromRegistration) {
        // User just completed registration, trust the fresh user data
        setPartnerStatus('submitted');
        setStatusLoading(false);
      } else {
        api.get(`/api/partners/${user.partner_id}`)
          .then(res => {
            setPartnerStatus(res.data?.partner?.status || null);
            setStatusLoading(false);
            // Load Business Portrait photo only when document is verified
            const photoDoc = res.data?.documents?.find(d => d.document_type === 'photo');
            if (photoDoc && photoDoc.status === 'verified') {
              api.get(`/api/documents/${photoDoc.id}/url`)
                .then(r => setHeaderPhotoUrl(r.data?.url || null))
                .catch(() => {});
            }
          })
          .catch(() => {
            setPartnerStatus(null);
            setStatusLoading(false);
          });
      }
    } else {
      // No partner_id means new user, needs registration
      setPartnerStatus('draft');
      setStatusLoading(false);
    }
  }, [user?.partner_id, location.state?.fromRegistration]);

  // Fetch RM info from dashboard API
  useEffect(() => {
    if (!statusLoading && partnerStatus !== 'draft' && user?.partner_id) {
      api.get('/api/partners/my/dashboard').then(res => {
        if (res.data?.assigned_rm) {
          setRmInfo(res.data.assigned_rm);
        }
      }).catch(() => {});
    }
  }, [statusLoading, partnerStatus, user?.partner_id]);

  // Fetch unread count on path change
  useEffect(() => { 
    if (!statusLoading && partnerStatus !== 'draft') {
      fetchUnreadCount(); 
    }
  }, [location.pathname, fetchUnreadCount, statusLoading, partnerStatus]);

  // Listen for notification updates
  useEffect(() => {
    const handler = () => fetchUnreadCount();
    window.addEventListener('notifications-updated', handler);
    return () => window.removeEventListener('notifications-updated', handler);
  }, [fetchUnreadCount]);

  // Calculate derived values
  const isOnRegistrationPage = location.pathname === '/partner/register';
  const needsRegistration = partnerStatus === 'draft' || !user?.partner_id;
  const partnerName = user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : 'Partner';
  
  // Sidebar should be disabled during registration
  const isSidebarDisabled = needsRegistration;
  const visibleSidebarLinks = sidebarLinks.filter(link => {
    if (!link.eligibleOnly) return true;
    return partnerStatus === 'approved' || partnerStatus === 'active';
  });

  // Show loading while checking partner status
  if (statusLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-[#0066CC] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // If partner is in draft status and not on registration page, redirect
  if (needsRegistration && !isOnRegistrationPage) {
    return <Navigate to="/partner/register" replace />;
  }

  // If partner already completed registration and tries to access registration page, redirect to dashboard
  if (!needsRegistration && isOnRegistrationPage) {
    return <Navigate to="/partner" replace />;
  }

  return (
    <div className="flex h-screen w-full overflow-hidden" data-testid="partner-layout">
      {/* SIDEBAR */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-[230px] bg-[#0B1A33] flex flex-col shrink-0 transform transition-transform lg:translate-x-0 lg:static ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Logo */}
        <div className="px-5 pt-6 pb-8">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="DhanSthal" className="h-9 w-auto object-contain" />
          </div>
        </div>
        {/* Mobile close button */}
        <button className="lg:hidden absolute top-5 right-4 text-[#6b8ab5] hover:text-white" onClick={() => setSidebarOpen(false)}>
          <X className="w-5 h-5" />
        </button>

        {/* Nav */}
        <nav className="flex-1 px-3 flex flex-col gap-[2px] overflow-y-auto">
          {visibleSidebarLinks.map(link => (
            isSidebarDisabled ? (
              <div
                key={link.to}
                className="flex items-center gap-3 px-3 py-[10px] rounded-lg text-[13px] font-medium text-[#5a6a7e] cursor-not-allowed opacity-50"
                title="Complete registration to access"
              >
                {link.icon}
                {link.label}
              </div>
            ) : (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-[10px] rounded-lg text-[13px] font-medium transition-colors ${
                    isActive
                      ? 'bg-[#1B6EF3] text-white'
                      : 'text-[#8DA4BF] hover:text-white hover:bg-[#122a4e]'
                  }`
                }
                onClick={() => setSidebarOpen(false)}
              >
                {link.icon}
                {link.label}
              </NavLink>
            )
          ))}
        </nav>

        {/* Logout - mobile only */}
        <div className="px-3 pb-4 pt-3 border-t border-[#1a3050] mt-auto lg:hidden">
          <button onClick={() => { setSidebarOpen(false); logout(); navigate('/login'); }} className="w-full flex items-center gap-3 px-3 py-2.5 text-[13px] text-red-400 font-medium rounded-lg hover:bg-[#122a4e] transition-colors">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            Logout
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && <button aria-label="Close sidebar" className="fixed inset-0 bg-black/50 z-30 lg:hidden w-full h-full" onClick={() => setSidebarOpen(false)} />}

      {/* MAIN */}
      <main className="flex-1 flex flex-col min-w-0 overflow-y-auto bg-[#F5F7FA] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {/* Header */}
        <header className="sticky top-0 z-10 bg-white border-b border-[#E8ECF0] px-3 sm:px-4 lg:px-6 h-[56px] sm:h-[60px] flex items-center gap-3 sm:gap-4 shrink-0">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
            <Menu className="w-5 h-5 text-[#64748b]" />
          </button>

          <div className="flex-1 min-w-0">
            <h1 className="text-[16px] font-semibold text-[#1e293b] leading-tight">{pageTitle}</h1>
            <p className="text-[12px] text-[#64748b]">Welcome back, {partnerName}</p>
          </div>

          <div className="flex items-center gap-2">
            {/* Connect to RM Button - Desktop */}
            {rmInfo && (
              <div className="relative hidden sm:block">
                <button 
                  onClick={() => setRmDropdownOpen(!rmDropdownOpen)} 
                  className="flex items-center gap-2 h-9 px-4 rounded-xl bg-[#0066CC] hover:bg-[#005bb5] text-white text-[12px] font-medium transition-colors shadow-sm"
                >
                  <User className="w-4 h-4" />
                  <span className="hidden lg:inline">My RM</span>
                </button>

                {rmDropdownOpen && (
                  <>
                    <button aria-label="Close RM menu" className="fixed inset-0 z-40 w-full h-full" onClick={() => setRmDropdownOpen(false)} />
                    <div className="absolute top-full right-0 mt-2 w-[260px] rounded-2xl border border-slate-200/80 bg-white shadow-[0_10px_30px_rgba(2,8,23,0.08)] z-50 overflow-hidden">
                      {/* RM Header */}
                      <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
                        <p className="text-[10px] uppercase tracking-wide text-slate-500 font-medium mb-1">My Relationship Manager</p>
                        <p className="text-[14px] font-semibold text-[#1e293b]">{rmInfo.name}</p>
                      </div>
                      {/* Contact Actions */}
                      <div className="p-3 flex flex-col gap-2">
                        {rmInfo.email && (
                          <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200">
                            <a 
                              href={`mailto:${rmInfo.email}`} 
                              className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity"
                              onClick={() => setRmDropdownOpen(false)}
                            >
                              <div className="w-8 h-8 rounded-lg bg-[#0066CC] flex items-center justify-center shrink-0">
                                <Mail className="w-4 h-4 text-white" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-[11px] text-slate-500">Email</p>
                                <p className="text-[12px] font-medium text-[#1e293b] truncate">{rmInfo.email}</p>
                              </div>
                            </a>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(rmInfo.email);
                                toast.success('Email copied!');
                              }}
                              className="w-8 h-8 rounded-lg hover:bg-slate-200 flex items-center justify-center shrink-0 transition-colors"
                              aria-label="Copy email"
                            >
                              <Copy className="w-4 h-4 text-slate-500" />
                            </button>
                          </div>
                        )}
                        {rmInfo.phone && (
                          <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200">
                            <a 
                              href={`tel:${rmInfo.phone}`} 
                              className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity"
                              onClick={() => setRmDropdownOpen(false)}
                            >
                              <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center shrink-0">
                                <Phone className="w-4 h-4 text-white" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-[11px] text-slate-500">Call</p>
                                <p className="text-[12px] font-medium text-[#1e293b]">{rmInfo.phone}</p>
                              </div>
                            </a>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(rmInfo.phone);
                                toast.success('Phone number copied!');
                              }}
                              className="w-8 h-8 rounded-lg hover:bg-slate-200 flex items-center justify-center shrink-0 transition-colors"
                              aria-label="Copy phone"
                            >
                              <Copy className="w-4 h-4 text-slate-500" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Connect to RM Icon - Mobile */}
            {rmInfo && (
              <div className="relative sm:hidden">
                <button 
                  onClick={() => setRmDropdownOpen(!rmDropdownOpen)} 
                  className="relative w-9 h-9 rounded-lg bg-[#0066CC] hover:bg-[#005bb5] flex items-center justify-center transition-colors"
                  aria-label="My RM"
                >
                  <User className="w-[18px] h-[18px] text-white" />
                </button>

                {rmDropdownOpen && (
                  <>
                    <button aria-label="Close RM menu" className="fixed inset-0 z-40 w-full h-full" onClick={() => setRmDropdownOpen(false)} />
                    <div className="absolute top-full right-0 mt-2 w-[260px] rounded-2xl border border-slate-200/80 bg-white shadow-[0_10px_30px_rgba(2,8,23,0.08)] z-50 overflow-hidden">
                      {/* RM Header */}
                      <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
                        <p className="text-[10px] uppercase tracking-wide text-slate-500 font-medium mb-1">My Relationship Manager</p>
                        <p className="text-[14px] font-semibold text-[#1e293b]">{rmInfo.name}</p>
                      </div>
                      {/* Contact Actions */}
                      <div className="p-3 flex flex-col gap-2">
                        {rmInfo.email && (
                          <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200">
                            <a 
                              href={`mailto:${rmInfo.email}`} 
                              className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity"
                              onClick={() => setRmDropdownOpen(false)}
                            >
                              <div className="w-8 h-8 rounded-lg bg-[#0066CC] flex items-center justify-center shrink-0">
                                <Mail className="w-4 h-4 text-white" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-[11px] text-slate-500">Email</p>
                                <p className="text-[12px] font-medium text-[#1e293b] truncate">{rmInfo.email}</p>
                              </div>
                            </a>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(rmInfo.email);
                                toast.success('Email copied!');
                              }}
                              className="w-8 h-8 rounded-lg hover:bg-slate-200 flex items-center justify-center shrink-0 transition-colors"
                              aria-label="Copy email"
                            >
                              <Copy className="w-4 h-4 text-slate-500" />
                            </button>
                          </div>
                        )}
                        {rmInfo.phone && (
                          <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200">
                            <a 
                              href={`tel:${rmInfo.phone}`} 
                              className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity"
                              onClick={() => setRmDropdownOpen(false)}
                            >
                              <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center shrink-0">
                                <Phone className="w-4 h-4 text-white" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-[11px] text-slate-500">Call</p>
                                <p className="text-[12px] font-medium text-[#1e293b]">{rmInfo.phone}</p>
                              </div>
                            </a>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(rmInfo.phone);
                                toast.success('Phone number copied!');
                              }}
                              className="w-8 h-8 rounded-lg hover:bg-slate-200 flex items-center justify-center shrink-0 transition-colors"
                              aria-label="Copy phone"
                            >
                              <Copy className="w-4 h-4 text-slate-500" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            <button 
              className={`relative w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${isSidebarDisabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-[#f1f5f9]'}`} 
              onClick={() => !isSidebarDisabled && navigate('/partner/notifications')} 
              aria-label="Notifications"
              disabled={isSidebarDisabled}
              title={isSidebarDisabled ? 'Complete registration to access' : 'Notifications'}
            >
              <Bell className="w-[18px] h-[18px] text-[#64748b]" />
              {!isSidebarDisabled && unreadCount > 0 && <span className="absolute top-0.5 right-0.5 w-[16px] h-[16px] bg-[#f97316] text-white text-[9px] font-bold rounded-full flex items-center justify-center">{unreadCount}</span>}
            </button>
            <div className="relative hidden sm:block">
              <button className="flex items-center gap-2.5 pl-3 ml-1 border-l border-[#E8ECF0] cursor-pointer" onClick={() => setProfileOpen(!profileOpen)} aria-label="Toggle profile menu" aria-expanded={profileOpen}>
                <div className="w-8 h-8 rounded-full bg-[#1B6EF3] flex items-center justify-center text-white text-[11px] font-bold shrink-0 overflow-hidden">
                  {headerPhotoUrl
                    ? <img src={headerPhotoUrl} alt={partnerName} className="w-full h-full object-cover" />
                    : partnerName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div className="text-right">
                  <div className="text-[13px] font-semibold text-[#1e293b] leading-tight">{partnerName}</div>
                </div>
                <ChevronDown className={`w-3.5 h-3.5 text-[#94a3b8] transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
              </button>

              {profileOpen && (
                <>
                  <button aria-label="Close profile menu" className="fixed inset-0 z-40 w-full h-full" onClick={() => setProfileOpen(false)} />
                  <div className="absolute top-full right-0 mt-2 w-[260px] bg-white border border-[#E2E8F0] rounded-xl shadow-lg z-50 overflow-hidden">
                    {/* Profile Info */}
                    <div className="px-4 py-4 bg-[#f8fafc] border-b border-[#E2E8F0]">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#1B6EF3] flex items-center justify-center text-white text-[13px] font-bold shrink-0 overflow-hidden">
                          {headerPhotoUrl
                            ? <img src={headerPhotoUrl} alt={partnerName} className="w-full h-full object-cover" />
                            : partnerName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="text-[14px] font-semibold text-[#1e293b] truncate">{partnerName}</div>
                          <div className="text-[12px] text-[#64748b] truncate">{user?.email || ''}</div>
                        </div>
                      </div>
                    </div>
                    {/* Details */}
                    <div className="px-4 py-3 space-y-2 border-b border-[#E2E8F0]">
                      <div className="flex items-center gap-2.5 text-[12px] text-[#475569]">
                        <svg className="w-4 h-4 text-[#94a3b8]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                        Role: <span className="font-medium text-[#1e293b]">Partner</span>
                      </div>
                      <div className="flex items-center gap-2.5 text-[12px] text-[#475569]">
                        <svg className="w-4 h-4 text-[#94a3b8]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
                        Partner ID: <span className="font-medium text-[#1e293b]">{user?.partner_code || '-'}</span>
                      </div>
                    </div>
                    {/* Logout */}
                    <div className="px-3 py-2">
                      <button onClick={() => { setProfileOpen(false); logout(); navigate('/login'); }} className="w-full flex items-center gap-2.5 px-3 py-2.5 text-[13px] text-red-600 font-medium rounded-lg hover:bg-red-50 transition-colors">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                        Logout
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="p-3 sm:p-4 lg:p-5 flex-1">
          <Suspense fallback={<div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-3 border-[#2563eb] border-t-transparent rounded-full animate-spin" /></div>}>
            <Outlet />
          </Suspense>
        </div>
      </main>
    </div>
  );
}
